import type { CotacaoEnvioAr } from '@/types/arbitral/envioArTypes';

import { ehErroHttp } from '@/shared/utils/erroHttp';

/**
 * Códigos que o backend devolve no confirm. A decisão é SEMPRE por código, nunca
 * por texto da mensagem — copy muda, e um `includes('não elegível')` quebraria sem
 * nada acusar.
 */
export const CODIGOS_SOLICITACAO_AR = {
  /** Saiu da fila: processo fora de citação, ciência confirmada, participação encerrada. */
  REQUERIDO_NAO_ELEGIVEL: 'REQUERIDO_NAO_ELEGIVEL',
  /** Já existe uma solicitação aberta para este requerido. */
  SOLICITACAO_JA_ABERTA: 'SOLICITACAO_JA_ABERTA',
  /** O valor exibido não é mais a cotação vigente. */
  COTACAO_DEFASADA: 'COTACAO_DEFASADA',
  /** Processo sem requerente ativo — não há quem autorize. */
  REQUERENTE_AUSENTE: 'REQUERENTE_AUSENTE',
  /** A transportadora recusou o destino. */
  CEP_NAO_ATENDIDO: 'CEP_NAO_ATENDIDO',
  /** Falha transitória ao cotar na transportadora. */
  COTACAO_INDISPONIVEL: 'COTACAO_INDISPONIVEL',
  /**
   * O CEP não existe. Vem da cotação também, e não só do campo de CEP: o
   * `POST /ar/cotacoes` consulta o CEP antes de cotar, porque a transportadora
   * exige cidade/UF no pedido.
   */
  CEP_NAO_ENCONTRADO: 'CEP_NAO_ENCONTRADO',
  /** Os Correios não responderam — indisponibilidade nossa, nunca do endereço. */
  CEP_INDISPONIVEL: 'CEP_INDISPONIVEL',
  /**
   * O modelo de carta escolhido não existe, foi inativado, ou é de outra empresa.
   * Os três casos vêm com o MESMO código: distinguir "não existe" de "é de outro
   * tenant" confirmaria a existência de um id alheio.
   */
  MODELO_CARTA_INVALIDO: 'MODELO_CARTA_INVALIDO',
  /** O prazo impresso na carta saiu da faixa aceita (1 a 60 dias úteis). */
  PRAZO_CARTA_INVALIDO: 'PRAZO_CARTA_INVALIDO',
  /**
   * O requerente recusou o envio: a Secretaria não abre nova rodada até ele desfazer a
   * recusa. Chega no confirm quando a recusa acontece com o drawer aberto.
   */
  REQUERENTE_RECUSOU_ENVIO: 'REQUERENTE_RECUSOU_ENVIO',
  /**
   * A cobrança desta solicitação não está em estado de reemissão — já foi paga,
   * está de pé com o link na mão do requerente, ou ainda nem foi emitida.
   */
  COBRANCA_NAO_REEMITIVEL: 'COBRANCA_NAO_REEMITIVEL',
  /**
   * A requisição não chegou a ter resposta HTTP: rede caída, servidor fora,
   * CORS. Código do CLIENTE — o servidor nunca o emite. Existe para a tela poder
   * tratar isso como falha transitória em vez de despejar o texto do navegador
   * ("Failed to fetch") na cara da secretaria.
   */
  REDE_INDISPONIVEL: 'REDE_INDISPONIVEL',
} as const;

/**
 * Códigos de cotação que são VEREDITO sobre o destino: repetir devolve o mesmo.
 * A saída é corrigir o CEP, então a tela não oferece "tentar de novo" neles — um
 * botão ali empurraria a secretaria para um laço que nunca termina.
 */
const VEREDITOS_DE_COTACAO: readonly string[] = [
  CODIGOS_SOLICITACAO_AR.CEP_NAO_ENCONTRADO,
  CODIGOS_SOLICITACAO_AR.CEP_NAO_ATENDIDO,
];

/** Falha transitória — insistir tem chance de dar certo. */
export function cotacaoPodeSerRefeita(codigo: string | null): boolean {
  return codigo === null || !VEREDITOS_DE_COTACAO.includes(codigo);
}

/**
 * O que a secretaria tem de FAZER, quando a ação não é o próprio botão.
 * `null` no transitório: ali a orientação é o "Tentar de novo" ao lado, e repetir
 * "tente em instantes" depois de a mensagem do servidor já dizer isso é ruído.
 */
export function orientacaoErroCotacao(codigo: string | null): string | null {
  if (codigo === CODIGOS_SOLICITACAO_AR.CEP_NAO_ENCONTRADO) {
    return 'Confira o número do CEP digitado.';
  }
  if (codigo === CODIGOS_SOLICITACAO_AR.CEP_NAO_ATENDIDO) {
    return 'Confira o CEP ou escolha outro endereço de entrega.';
  }
  return null;
}

/**
 * O requerido saiu da fila enquanto o drawer estava aberto.
 *
 * Os dois códigos viram o mesmo tratamento na tela (avisa e avança para o próximo
 * do lote) porque a ação da secretaria é idêntica — o que muda é só a mensagem.
 */
export function saiuDaFila(erro: unknown): boolean {
  if (!ehErroHttp(erro)) return false;
  return (
    erro.codigo === CODIGOS_SOLICITACAO_AR.REQUERIDO_NAO_ELEGIVEL ||
    erro.codigo === CODIGOS_SOLICITACAO_AR.SOLICITACAO_JA_ABERTA ||
    erro.status === 404
  );
}

/**
 * O servidor recotou no ato do confirm e o valor mudou.
 *
 * Carrega a cotação NOVA para a tela repovoar sem uma segunda ida ao servidor — e,
 * principalmente, sem deixar a secretaria confirmar sobre um valor defasado.
 */
export class CotacaoDefasadaError extends Error {
  readonly cotacaoAtual: CotacaoEnvioAr | null;

  constructor(mensagem: string, cotacaoAtual: CotacaoEnvioAr | null) {
    super(mensagem);
    this.name = 'CotacaoDefasadaError';
    this.cotacaoAtual = cotacaoAtual;
  }
}
